import React, {
  MouseEventHandler,
  useCallback,
  useMemo,
  useState
} from "react";
import classNames from "classnames";
import GenericFlyout from "../modal/GenericFlyout";
import { usePathLanguages, useSettings } from "../../hooks/SettingsHooks";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import { useLocation, useSearchParams } from "react-router-dom";
import { getFlag } from "../../utils/i18nUtils";
import { getCurrentSite, siteUrl, switchSite } from "../../utils/SiteUtils";
import { Settings } from "../../types/Settings";
import { useLang } from "../../hooks/LangHooks";
import cloneDeep from "lodash/cloneDeep";
import { Entries } from "type-fest";
import { Language } from "../../config/languages";
import { Site } from "../../config/sites";
import "./loc.scss";
import { Badge } from "reactstrap";
import { getPathLanguage } from "../../utils/LangUtils";

const LocDropdown = () => {
  const settings: Settings = useSettings();
  const { sites: _sites } = settings;
  const languages = usePathLanguages();
  const { currentRegisteredPaths, pageAvailableOnSites } =
    useNavigationContext();
  const paths = currentRegisteredPaths;
  const availableOnSites = pageAvailableOnSites;
  const isAvailableOnSite = useCallback(
    (site: Site) =>
      availableOnSites === undefined || availableOnSites.indexOf(site) >= 0,
    [availableOnSites]
  );
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString() ? "?" + searchParams.toString() : "";
  const curLang = useLang();
  const t = useBjelinTranslations();
  const ua = navigator.userAgent;
  const isSafariMobile =
    ua.indexOf("Safari") > -1 &&
    ua.indexOf("Chrom") === -1 &&
    ua.indexOf("Mobile") === -1;
  const [showLocMenu, setShowLocMenu] = useState(false);
  const sites = useMemo(() => {
    const sites = cloneDeep(_sites);
    const siteEntries = Object.entries(sites) as Entries<typeof sites>;
    siteEntries.forEach(([_site, siteData]) => {
      siteData.languages = siteData.languages.map(lang =>
        getPathLanguage(lang)
      );
    });
    return sites;
  }, [_sites]);
  const currentSiteData = useMemo(() => sites[getCurrentSite()], [sites]);
  const siteEntries = useMemo(
    () => Object.entries(sites) as Entries<typeof sites>,
    [sites]
  );
  const closeLocMenu = useCallback(() => setShowLocMenu(false), []);
  const toggleLocMenu = useCallback(
    () => setShowLocMenu(showLocMenu => !showLocMenu),
    []
  );
  const swapSiteWithCookie =
    (site: Site): MouseEventHandler =>
    e => {
      const href = e.currentTarget.getAttribute("href");
      if (href) {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        switchSite(site as Site, new URL(href));
      }
    };

  return (
    <>
      <button
        id="loc-menu-button"
        title={t("menu.tooltip.language")}
        aria-haspopup="dialog"
        className="nav-link"
        style={{
          backgroundColor: showLocMenu ? "#fff" : "transparent",
          border: 0
        }}
        aria-expanded={showLocMenu}
        onClick={toggleLocMenu}
      >
        <span
          className="flagIcon"
          style={{ marginRight: 0, marginTop: "1.25rem" }}
        >
          <img src={getFlag(getCurrentSite(), curLang)} alt="" />
        </span>
        {getCurrentSite() === "stage" && (
          <Badge
            color="danger"
            style={{ color: "#fff", right: 0 }}
            pill
            title="Staging site"
          >
            S
          </Badge>
        )}
      </button>
      <GenericFlyout
        id="loc-dropdown"
        isOpen={showLocMenu}
        close={closeLocMenu}
        noCloseBtn
        align={"top"}
        width={20}
        ariaLabel={t("menu.tooltip.language")}
      >
        <div
          className={"loc-menu px-4 py-3"}
          style={{
            height: "fit-content",
            maxHeight: `calc(100dvh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - var(--menu-height) - var(--padding-top) - ${
              isSafariMobile ? 64 : 0
            }px)`,
            background: "#fff",
            overflowY: "auto",
            overflowX: "hidden"
          }}
        >
          {languages.length > 1 ? (
            <>
              <h4>{currentSiteData.name.toUpperCase()}</h4>
              <ul className="lang-menu">
                {languages.map(lang => (
                  <li
                    className={classNames({
                      selected: lang === curLang
                    })}
                    key={lang}
                  >
                    <a
                      data-site={getCurrentSite()}
                      data-lang={lang}
                      href={`/${lang}${
                        paths[lang as Language] ||
                        paths["en"] ||
                        paths["all"] ||
                        location.pathname
                      }${search}`}
                    >
                      <span className="flagIcon">
                        <img src={getFlag(getCurrentSite(), lang)} alt="" />
                      </span>
                      <span className="label">{t("language." + lang)}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <h4 className="selected">
              <a
                data-site={getCurrentSite()}
                data-lang={languages[0]}
                href={siteUrl(getCurrentSite(), languages[0])}
              >
                <span className="flagIcon">
                  <img src={getFlag(getCurrentSite(), languages[0])} alt="" />
                </span>
                {currentSiteData.name.toUpperCase()}
              </a>
            </h4>
          )}

          {siteEntries
            .filter(([site]) => site !== getCurrentSite())
            .map(([site, siteData]) => (
              <div
                key={site}
                style={{
                  margin: siteData.languages.length > 1 ? "1em 0" : 0
                }}
              >
                {siteData.languages.length > 1 ? (
                  <>
                    <h4>{siteData.name.toUpperCase()}</h4>
                    <ul className="lang-menu">
                      {siteData.languages.map(lang => (
                        <li key={lang}>
                          <a
                            data-site={site}
                            data-lang={lang}
                            href={
                              siteUrl(site, lang, undefined, true) +
                              (isAvailableOnSite(site)
                                ? (paths[lang] ||
                                    paths["en"] ||
                                    paths["all"] ||
                                    "/") + search
                                : "/")
                            }
                            onClick={swapSiteWithCookie(site)}
                          >
                            <span className="flagIcon">
                              <img src={getFlag(site, lang)} alt="" />
                            </span>
                            <span className="label">
                              {t("language." + lang)}
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <h4>
                    <a
                      data-site={site}
                      data-lang={siteData.languages[0]}
                      href={
                        siteUrl(site, siteData.languages[0], undefined, true) +
                        (isAvailableOnSite(site)
                          ? (paths[siteData.languages[0]] ||
                              paths["en"] ||
                              paths["all"] ||
                              "/") + search
                          : "/")
                      }
                      onClick={swapSiteWithCookie(site)}
                    >
                      <span className="flagIcon">
                        <img
                          src={getFlag(site, siteData.languages[0])}
                          alt=""
                        />
                      </span>
                      {siteData.name.toUpperCase()}
                    </a>
                  </h4>
                )}
              </div>
            ))}
        </div>
      </GenericFlyout>
    </>
  );
};

export default LocDropdown;
